import { selectPartner360Data } from 'legacy/lut/features/partner360/configuration'
import { IState } from 'legacy/lut/store'
import { FC, useCallback, useLayoutEffect, useRef } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations/components/translation'
import { LoaderOverlay } from 'core-components/library/loader'
import { ModalDialog } from 'core-components/library/modalDialog'
import { Partner360FontProvider } from 'partner360/components/typography/Partner360FontProvider'

import { TRANSLATION_KEYS as tr } from '../../../translations'

import { KeyInitiativeStepsContent } from './components/keyInitiativeStepsContent/KeyInitiativeStepsContent'
import { IKeyInitiativeEditorContentProps } from './models/IKeyInitiativeEditorContentProps'

import styles from './KeyInitiativeStepsModal.module.scss'

interface KeyInitiativeStepsModalProps extends IKeyInitiativeEditorContentProps {
  isOpen: boolean
  isSaving?: boolean
  onBusyStateChange: (isBusy: boolean) => void
  keyInitiativesFormat: number
  setKeyInitiativesFormat: (newValue: number) => void
}

export const KeyInitiativeStepsModal: FC<KeyInitiativeStepsModalProps> = ({
  initialData,
  partnerId,
  programType,
  onApply,
  isCommercial,
  isDataInsightPresent,
  isDataInsightTab,
  isAmplifyImpactInfoSection,
  onCancel,
  onBusyStateChange,
  saveAsNew,
  keyObjectivesFilter,
  pendingKeyInitiative,
  finSobData,
  otherInitiatives,

  isOpen,
  isSaving = false,
  keyInitiativesFormat,
  setKeyInitiativesFormat,

  fYear,
  businessPriorities,
  keyObjectives,
  keyObjectiveTags,
  initiativeFormKey,
  parentFormKey,
}) => {
  const partnerName = useSelector((state: IState) => selectPartner360Data(state).partnerName)
  const busyRef = useRef(isSaving)

  useLayoutEffect(() => {
    busyRef.current = isSaving
  }, [isSaving])

  const handleBusyStateChange = useCallback(
    (isBusy: boolean) => {
      // Block closing immediately, before the parent's busy prop has re-rendered.
      busyRef.current = isBusy
      onBusyStateChange(isBusy)
    },
    [onBusyStateChange]
  )

  const handleCancel = useCallback(() => {
    if (!busyRef.current) {
      onCancel()
    }
  }, [onCancel])

  const modalHeader = useCallback(() => {
    return (
      <>
        <div className={styles.title}>
          <Translation text={tr.keyInitiativeEditor.keyInitiativeTitle} span />
          {partnerName && <span className={styles.partnerName}>{partnerName}</span>}
        </div>
        {/* Outside the scrolling body: the modal window is the overlay's containing block. */}
        {isSaving && <LoaderOverlay show className={styles.savingOverlay} />}
      </>
    )
  }, [isSaving, partnerName])

  if (!isOpen) {
    return null
  }

  return (
    <Partner360FontProvider>
      <ModalDialog
        show={isOpen}
        onClose={handleCancel}
        classes={{
          overlay: styles.modalOverlay,
          window: styles.modalWindow,
          body: styles.modalBody,
          closeButton: styles.closeButton,
        }}
        header={modalHeader}
        closeOnOutsideClick={false}
        content={KeyInitiativeStepsContent}
        size="md"
        contentProps={{
          keyObjectivesFilter,
          keyInitiativesFormat,
          setKeyInitiativesFormat,
          isDataInsightPresent,
          isDataInsightTab,
          isAmplifyImpactInfoSection,
          isCommercial,
          partnerId,
          initialData,
          programType,
          onApply,
          onCancel: handleCancel,
          onBusyStateChange: handleBusyStateChange,
          saveAsNew,
          pendingKeyInitiative,
          finSobData,
          otherInitiatives,
          fYear,
          businessPriorities,
          keyObjectives,
          keyObjectiveTags,
          initiativeFormKey,
          parentFormKey,
        }}
      />
    </Partner360FontProvider>
  )
}
